/**
 * This generic chart gives dashboard aggregates fixed-coordinate horizontal
 * bars without inheriting entity-ranking limits or title conventions.
 */
import React from 'react';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { colorBlue400 } from '@amzn/meridian-tokens/base/color';
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryContainer,
} from 'victory';
import { useTranslation } from 'src/arb-wrapper';
import { skeletonBase } from 'src/components/entity-metrics/skeleton';

const CHART_HEIGHT = 180;
const CHART_WIDTH = 400;
const LABEL_CHARACTER_WIDTH = 6;
const MIN_CATEGORY_PADDING = 64;
const MAX_CATEGORY_PADDING = 180;
const MIN_VALUE_PADDING = 48;
const MAX_VALUE_PADDING = 130;

export interface HorizontalBarChartProps {
  readonly data: Array<{ label: string; value: number }>;
  readonly title: string;
  readonly valueFormatter?: (value: number) => string;
  readonly isLoading?: boolean;
  readonly minHeight?: number | string;
}

const getLabelPadding = (
  labels: readonly string[],
  minimum: number,
  maximum: number,
  extra: number,
): number => {
  const longestLabel = labels.reduce(
    (longest, label) => Math.max(longest, label.length),
    0,
  );
  return Math.min(
    maximum,
    Math.max(minimum, longestLabel * LABEL_CHARACTER_WIDTH + extra),
  );
};

export const HorizontalBarChart: React.FC<HorizontalBarChartProps> = ({
  data,
  title,
  valueFormatter,
  isLoading,
  minHeight,
}) => {
  const { t } = useTranslation();
  const sorted = [...data].sort((left, right) => right.value - left.value);
  const formatValue = valueFormatter ?? String;
  const valueAxisProps = valueFormatter === undefined
    ? {}
    : { tickFormat: valueFormatter };
  const padding = {
    bottom: 30,
    left: getLabelPadding(
      sorted.map(({ label }) => label),
      MIN_CATEGORY_PADDING,
      MAX_CATEGORY_PADDING,
      18,
    ),
    right: getLabelPadding(
      sorted.map(({ value }) => formatValue(value)),
      MIN_VALUE_PADDING,
      MAX_VALUE_PADDING,
      28,
    ),
    top: 10,
  };

  return (
    <Column
      backgroundColor="white"
      data-testid="horizontal-bar-chart"
      heights={['fit', 'fill']}
      minHeight={minHeight}
      minWidth={0}
      spacing="300"
      spacingInset="400"
      type="outline"
      width="100%"
    >
      <Heading level={4}>{title}</Heading>
      {isLoading
        ? (
        <Column data-testid="horizontal-bar-chart-skeleton" spacing="200">
          {[100, 80, 65, 50, 35].map((width, index) => (
            <Row key={index} spacing="200">
              <div
                style={{
                  background: '#f0f0f0',
                  borderRadius: 4,
                  height: 14,
                  width: 30,
                }}
              />
              <div
                style={{
                  ...skeletonBase,
                  height: 14,
                  width: `${width}%`,
                }}
              />
            </Row>
          ))}
        </Column>
          )
        : sorted.length === 0
          ? (
        <Text color="secondary">{t('chart_no_data_available')}</Text>
            )
          : (
        <div
          data-testid="horizontal-bar-chart-plot"
          style={{
            minHeight: CHART_HEIGHT,
            minWidth: 0,
            width: '100%',
          }}
        >
          <VictoryChart
            containerComponent={<VictoryContainer responsive />}
            domainPadding={{ x: 20 }}
            height={CHART_HEIGHT}
            padding={padding}
            width={CHART_WIDTH}
          >
            <VictoryAxis style={{ tickLabels: { fontSize: 10 } }} />
            <VictoryAxis
              {...valueAxisProps}
              dependentAxis
              style={{ tickLabels: { fontSize: 10 } }}
            />
            <VictoryBar
              data={sorted.map(({ label, value }) => ({ x: label, y: value }))}
              horizontal
              labels={({ datum }: { datum: { y: number } }) =>
                formatValue(datum.y)
              }
              style={{
                data: { fill: colorBlue400 },
                labels: { fontSize: 9 },
              }}
            />
          </VictoryChart>
        </div>
            )}
    </Column>
  );
};
